/**
 * F-28 "Original Script" locked-column rules, as the GRID needs them — PURE
 * logic (no React, no DOM, no Yjs).
 *
 * The lock is ENFORCED in the shared accessors: `setColumnName`, `deleteColumn`
 * and `moveColumn` refuse the locked column, and `moveColumn`/`addColumn` refuse
 * landing anything at/before it — all returning false having written nothing.
 * That is the guarantee, and nothing here is a security boundary. This module's
 * whole job is that the UI never OFFERS an action the accessors would swallow:
 * F-28 asks for "no rename affordance, greyed delete, drag-reorder refused",
 * and an affordance that looks live but does nothing is worse than none.
 *
 * Note the deliberate asymmetry with the shared `addColumn`, which CLAMPS an
 * insert-before-the-source to the source's right. The UI does not mirror that
 * clamp: a "+" that visibly sits LEFT of the source but drops a column on its
 * RIGHT is a small lie, and the user cannot tell whether they mis-clicked or
 * the app mis-fired. So the affordance is withheld instead (`canInsertBefore`),
 * and the clamp stays what it should be — a last-resort server-side backstop
 * for a racing insert, not a UI behavior.
 */

/** The boundaries a column reorder drag may legally drop at. */
export interface ReorderBounds {
  /** Lowest legal drop boundary. 1 when a locked source column sits leftmost. */
  min: number;
  /** Highest legal drop boundary (one past the last column). */
  max: number;
}

/**
 * Legal drop boundaries for a COLUMN reorder drag (F-28 half 2: "no other
 * column may be moved to a position at/before the locked column").
 *
 * Boundary `i` means "insert before the column currently at index i", so a
 * drop at boundary 0 lands left of the source column — exactly what
 * `moveColumn` refuses. With the source leftmost the first legal boundary is
 * 1. `sourceIndex` is the locked column's index, or -1 on a board seeded
 * before F-28 (no locked column ⇒ no constraint).
 */
export function columnReorderBounds(colCount: number, sourceIndex: number): ReorderBounds {
  return { min: sourceIndex < 0 ? 0 : sourceIndex + 1, max: colCount };
}

/** Clamp a drag's drop boundary into the legal range (F-28). */
export function clampReorderBoundary(boundary: number, bounds: ReorderBounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, boundary));
}

/**
 * F-28: may a NEW column be inserted at `index` (i.e. before the column
 * currently there)? False only for a slot at/before the locked source column,
 * which `addColumn` would clamp away. Callers use this to WITHHOLD the
 * affordance — the pill's left "+" on the source column, the gutter bubble on
 * its left boundary, and "Insert column left" in its menu — rather than to
 * relocate the user's click (see the module note).
 *
 * `sourceIndex` is -1 on a board seeded before F-28 ⇒ everything is insertable.
 */
export function canInsertColumnBefore(index: number, sourceIndex: number): boolean {
  return sourceIndex < 0 || index > sourceIndex;
}
